/**
 * Language configuration for SEO-optimized URLs
 *
 * This file defines the mapping between languages and their SEO-friendly URL prefixes.
 * Used by navigation components to generate proper links for each language.
 */

export interface LanguageConfig {
  locale: string;
  prefix: string;
  name: string;
}

export type LanguageCode = 'en' | 'de' | 'fr' | 'es';

export const languageConfig: Record<LanguageCode, LanguageConfig> = {
  en: {
    locale: 'en',
    prefix: '/engelberg-accommodation',
    name: 'English',
  },
  de: {
    locale: 'de',
    prefix: '/de/engelberg-ferienwohnung',
    name: 'Deutsch',
  },
  fr: {
    locale: 'fr',
    prefix: '/fr/hebergement-engelberg',
    name: 'Français',
  },
  es: {
    locale: 'es',
    prefix: '/es/alojamiento-engelberg',
    name: 'Español',
  },
};

/**
 * Gets the SEO-friendly URL for a given path and language
 *
 * @param path The path without language prefix (e.g., '/chalet')
 * @param language The language code (e.g., 'en', 'de')
 * @returns The SEO-friendly URL (e.g., '/engelberg-accommodation/chalet' for English)
 */
export function getSeoUrl(path: string, language: LanguageCode = 'en'): string {
  const config = languageConfig[language];

  // Handle root path
  if (path === '/') {
    return language === 'en' ? '/' : `/${language}`;
  }

  // Remove leading slash for path components
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;

  // For English, use the base prefix with the path
  if (language === 'en') {
    return `${config.prefix}/${cleanPath}`;
  }

  // For other languages, include the language code in the URL
  return `${config.prefix}/${cleanPath}`;
}

/**
 * Converts a legacy URL to an SEO-friendly URL
 *
 * @param legacyUrl The old URL format (e.g., '/chalet')
 * @param language The language code (e.g., 'en', 'de')
 * @returns The SEO-friendly URL
 */
export function convertLegacyUrl(
  legacyUrl: string,
  language: LanguageCode = 'en'
): string {
  // Language-specific path mappings
  const languageSpecificPaths: Record<LanguageCode, Record<string, string>> = {
    en: {
      // English paths stay the same
      '/chalet': 'chalet',
      '/gallery': 'gallery',
      '/attractions': 'attractions',
      '/transport': 'transport',
      '/map': 'map',
      '/navigation': 'navigation',
      '/ski-chalet': 'ski-chalet',
      '/activities': 'activities',
      '/activities/summer': 'activities/summer',
      '/activities/seasonal': 'activities/seasonal',
      '/hiking-trails': 'hiking-trails',
      '/family-accommodation': 'family-accommodation',
      '/sustainable-accommodation': 'sustainable-accommodation',
      '/booking/availability': 'booking/availability',
      '/booking/prices': 'booking/prices',
      '/booking/process': 'booking/process',
      '/booking/faq': 'booking/faq',
      '/reviews': 'reviews',
      '/contact': 'contact',
      '/contact/directions': 'contact/directions',
      '/contact/inquiry': 'contact/inquiry',
    },
    de: {
      // German localized paths
      '/chalet': 'chalet',
      '/gallery': 'galerie',
      '/attractions': 'sehenswuerdigkeiten',
      '/transport': 'transport',
      '/map': 'karte',
      '/navigation': 'navigation',
      '/ski-chalet': 'ski-chalet',
      '/activities': 'aktivitaeten',
      '/activities/summer': 'aktivitaeten/sommer',
      '/activities/seasonal': 'aktivitaeten/saisonal',
      '/hiking-trails': 'wanderwege',
      '/family-accommodation': 'familienunterkunft',
      '/sustainable-accommodation': 'nachhaltige-unterkunft',
      '/booking/availability': 'verfugbarkeit',
      '/booking/prices': 'preise',
      '/booking/process': 'buchungsprozess',
      '/booking/faq': 'haufige-fragen',
      '/reviews': 'bewertungen',
      '/contact': 'kontakt',
      '/contact/directions': 'kontakt/anfahrt',
      '/contact/inquiry': 'kontakt/anfrage',
    },
    es: {
      // Spanish localized paths
      '/chalet': 'chalet',
      '/gallery': 'galeria',
      '/attractions': 'atracciones',
      '/transport': 'transporte',
      '/map': 'mapa',
      '/navigation': 'navegacion',
      '/ski-chalet': 'chalet-de-esqui',
      '/activities': 'actividades',
      '/activities/summer': 'actividades/verano',
      '/activities/seasonal': 'actividades/estacional',
      '/hiking-trails': 'senderos',
      '/family-accommodation': 'alojamiento-familiar',
      '/sustainable-accommodation': 'alojamiento-sostenible',
      '/booking/availability': 'disponibilidad',
      '/booking/prices': 'precios',
      '/booking/process': 'proceso-de-reserva',
      '/booking/faq': 'preguntas-frecuentes',
      '/reviews': 'opiniones',
      '/contact': 'contacto',
      '/contact/directions': 'contacto/como-llegar',
      '/contact/inquiry': 'contacto/consulta',
    },
    fr: {
      // French localized paths
      '/chalet': 'chalet',
      '/gallery': 'galerie',
      '/attractions': 'attractions',
      '/transport': 'transport',
      '/map': 'carte',
      '/navigation': 'navigation',
      '/ski-chalet': 'chalet-de-ski',
      '/activities': 'activites',
      '/activities/summer': 'activites/ete',
      '/activities/seasonal': 'activites/saisonnier',
      '/hiking-trails': 'sentiers-de-randonnee',
      '/family-accommodation': 'hebergement-familial',
      '/sustainable-accommodation': 'hebergement-durable',
      '/booking/availability': 'disponibilite',
      '/booking/prices': 'prix',
      '/booking/process': 'processus-de-reservation',
      '/booking/faq': 'foire-aux-questions',
      '/reviews': 'avis',
      '/contact': 'contact',
      '/contact/directions': 'contact/itineraire',
      '/contact/inquiry': 'contact/demande',
    },
  };

  // Legacy URLs that should be mapped for all languages
  const commonLegacyUrlMap: Record<string, string> = {
    '/ski-chalet-engelberg': 'ski-chalet',
    '/family-accommodation-engelberg': 'family-accommodation',
  };

  // If the URL is already SEO-friendly, return it
  if (
    legacyUrl.includes('engelberg-accommodation') ||
    legacyUrl.includes('engelberg-ferienwohnung') ||
    legacyUrl.includes('hebergement-engelberg') ||
    legacyUrl.includes('alojamiento-engelberg')
  ) {
    return legacyUrl;
  }

  // Check language-specific mapping
  const localizedMap = languageSpecificPaths[language];
  if (localizedMap && localizedMap[legacyUrl]) {
    return getSeoUrl(localizedMap[legacyUrl], language);
  }

  // Check common legacy mappings (like ski-chalet-engelberg)
  if (commonLegacyUrlMap[legacyUrl]) {
    return getSeoUrl(commonLegacyUrlMap[legacyUrl], language);
  }

  // If it's an external URL (starts with http), return it as is
  if (legacyUrl.startsWith('http')) {
    return legacyUrl;
  }

  // For any other path, just append it to the language prefix
  return getSeoUrl(legacyUrl, language);
}
